/*
 * The password rules the browser may state, and no more. The policy of security.md §4.1 is the
 * server's (`internal/core/auth/password/policy.go`): twelve to 256 characters, not the account's
 * own handle or address, and not a password that stands in the breach corpus. The answer to a
 * submitted password is always the server's verdict.
 *
 * What the client owns is the checklist beside the field. It names the same three rules, so a person
 * who is choosing a password knows what is being asked *before* they are refused — and it never
 * claims a password is good: two of the three rules it can see for itself, the third one only the
 * server can, and the copy of that row says so.
 */

export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 256

/** The three refusals of the server's policy, in the order the checklist shows them. */
export type PasswordRuleId = "length" | "identifier" | "leak"

export interface PasswordRule {
	id: PasswordRuleId
	/** True when the browser can already see that the rule is kept; false while it cannot. */
	met: boolean
}

/**
 * What a password may not quote: the account's handle and the local part of its address, which is
 * what `Validate` compares against. An unknown one is simply left out, and the rule then stays
 * unticked — the server still applies it.
 */
export function forbiddenFragments(email?: string, handle?: string): string[] {
	const local = email === undefined ? "" : email.split("@")[0]
	return [local ?? "", handle ?? ""].filter((fragment) => fragment.length >= 3)
}

/**
 * The checklist for a password being chosen. `fragments` are the account's own identifiers when the
 * screen knows them; the length is counted in code points, as the server counts it after NFKC —
 * a difference that can only make the browser stricter than the server, never laxer.
 */
export function passwordRules(value: string, fragments: readonly string[] = []): PasswordRule[] {
	const folded = value.toLowerCase()
	return [
		{ id: "length", met: [...value].length >= PASSWORD_MIN_LENGTH },
		{
			id: "identifier",
			met:
				value !== "" &&
				fragments.length > 0 &&
				!fragments.some((fragment) => folded.includes(fragment.toLowerCase())),
		},
		// Only the server holds the corpus; the row says so rather than pretending to a verdict.
		{ id: "leak", met: false },
	]
}
